import type {ModalProps as MantineModalProps} from '@mantine/core';
import {modals} from '@mantine/modals';
import React from 'react';

import uniqueId from 'util/uniqueId';

/** Optional props forwarded to the Mantine `Modal` that wraps the content. */
export type RunModalContainerProps = Omit<Partial<MantineModalProps>, 'children' | 'opened' | 'onClose'> & {
  modalId?: string;
};

/** Any Modal Content used with LegacyRunModal should extend these props. */
export interface RunModalProps<T> {
  /** Close the modal and resolve the promise with `result`. */
  close: (result?: T) => void | Promise<void>;
}

export type RunModalReturnType<P> = P extends RunModalProps<infer T> ? T : void;

/** Imperatively open a Modal and return a Promise when the modal is open.
 *
 * The modal contents must accept a `close` prop that can be called to close the modal and resolve the
 * Promise with an optional result. If the modal is closed via the header close button or the Escape key,
 * the promise will resolve as undefined.
 *
 * @param ModalContentComponent - The component to render as the modal content.
 * @param contentProps - Props for the content of the Modal.
 * @param containerProps - Optional props for the Mantine Modal. (like `title`)
 *
 * ⚠️ Modals opened in this manner do not support dynamic content, i.e. the modal content will not change
 * if the props change.
 *
 * Generally Benchling prefers declarative Modal management by inserting the Modal as JSX in your component
 * and managing open state with `useDisclosure`. `runModal` is best suited for form-like modals that are not
 * url-navigable and can block the rest of the UI until closed.
 */
export default function runModal<C extends React.ComponentType<RunModalProps<unknown>>>(
  ModalContentComponent: C,
  /** Props for the content of the Modal */
  contentProps: Omit<React.ComponentProps<C>, 'close'>,
  /** Optional props for the Mantine Modal itself */
  containerProps?: RunModalContainerProps
): Promise<RunModalReturnType<React.ComponentProps<C>> | undefined> {
  return new Promise((resolve) => {
    const {modalId = uniqueId('run-modal-mantine'), ...mantineModalProps} = containerProps ?? {};

    let returnValue: RunModalReturnType<React.ComponentProps<C>> | undefined;

    function close(result?: RunModalReturnType<React.ComponentProps<C>>): void {
      returnValue = result;
      modals.close(modalId);
    }

    modals.open({
      ...mantineModalProps,
      modalId,
      onClose: () => {
        resolve(returnValue);
      },
      children: React.createElement(ModalContentComponent, {...contentProps, close}),
    });
  });
}
